import { Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { ShowPerformance } from '../../../models/showperformance.model';
import { User } from '../../../models/user.model';
import { AvailabilityReturn } from '../availability-return.model';
import { UserService } from '../../../services/user.service';
import { WeekDividerUtils } from '../../shared/utils/week-divider.utils';

interface ScheduleEvent {
    performance: ShowPerformance;
    toneelmeester: User;
}

export interface PerformanceWithWeekInfo extends ShowPerformance {
    weekNumber: number;
    showWeekDivider: boolean;
    dateKey: string;  // YYYY-MM-DD for grouping
    showDayDivider: boolean;  // true if this is first performance of the day
    isLastOfDay: boolean;  // true if this is last performance of the day
}

@Component({
    selector: 'app-planner-performance-list',
    templateUrl: './planner-performance-list.component.html',
    styleUrls: ['./planner-performance-list.component.scss'],
    standalone: false
})
export class PlannerPerformanceListComponent {
    @Input() public availabilities: AvailabilityReturn[] = [];
    @Input() public toneelmeesters: User[] = [];
    @Input() public allowEdit: boolean = false;

    @Output() toneelmeesterScheduleRequested = new EventEmitter<ScheduleEvent>();
    protected userCalendarToken: string | null = null;
    protected performancesWithWeekInfo: PerformanceWithWeekInfo[] = [];

    private readonly userService: UserService = inject(UserService);
    private _performances: ShowPerformance[] = [];

    @Input()
    set performances(value: ShowPerformance[]) {
        this._performances = value || [];
        // Pre-compute week dividers when performances change
        const withWeekInfo = WeekDividerUtils.addWeekDividerInfo(
            this._performances,
            p => p.datetime
        );

        // Add day grouping info
        this.performancesWithWeekInfo = this.addDayGroupingInfo(withWeekInfo as PerformanceWithWeekInfo[]);
    }

    private addDayGroupingInfo(performances: PerformanceWithWeekInfo[]): PerformanceWithWeekInfo[] {
        let previousDateKey = '';

        // First pass: add dateKey and showDayDivider
        for (const p of performances) {
            const dateKey = p.datetime.split('T')[0];
            p.dateKey = dateKey;
            p.showDayDivider = dateKey !== previousDateKey;
            p.isLastOfDay = false; // Will be set in second pass
            previousDateKey = dateKey;
        }

        // Second pass: mark last performance of each day
        for (let i = 0; i < performances.length; i++) {
            const current = performances[i];
            const next = performances[i + 1];

            // It's last of day if there's no next, or next is a different day
            if (!next || next.dateKey !== current.dateKey) {
                current.isLastOfDay = true;
            }
        }

        return performances;
    }

    get performances(): ShowPerformance[] {
        return this._performances;
    }

    public async getWhoAmI() {
        // TODO: This should be in the UserService I think...
        try {
            this.userCalendarToken = await this.userService.whoAmI();
        } catch (e) {
            console.error(e);
        }
    }

    protected emitClick(performance: ShowPerformance, toneelmeester: User): void {
        this.toneelmeesterScheduleRequested.emit({
            performance,
            toneelmeester
        });
    }

    protected trackByPerformanceId(index: number, performance: PerformanceWithWeekInfo): number {
        return performance.id;
    }

}
